import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { useFullscreenHost } from '@/lib/fullscreen'
import { cx } from '@/lib/cx'
import { InboxPage } from './InboxPage'
import styles from './InboxButton.module.css'

/** Входящие поверх текущего чата: открытие не меняет экран и историю переходов. */
export function InboxButton({ children, className, activeClassName, label = 'Входящие', tooltip = false }: {
  children: ReactNode
  className: string | undefined
  activeClassName: string | undefined
  label?: string
  tooltip?: boolean
}) {
  const [open, setOpen] = useState(false)
  const trigger = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const id = useId()
  const host = useFullscreenHost()
  const close = useCallback((refocus = false) => {
    setOpen(false)
    if (refocus) trigger.current?.focus({ preventScroll: true })
  }, [])

  useLayoutEffect(() => {
    if (!open) return
    const el = panel.current
    const anchor = trigger.current?.getBoundingClientRect()
    if (!el || !anchor) return
    if (window.innerWidth > 767) {
      const top = Math.min(anchor.bottom + 8, window.innerHeight - 160)
      el.style.top = `${Math.max(8, top)}px`
      el.style.left = `${Math.max(8, Math.min(anchor.right - el.offsetWidth, window.innerWidth - el.offsetWidth - 8))}px`
      el.style.maxHeight = `${Math.max(120, window.innerHeight - Math.max(8, top) - 12)}px`
    }
    el.querySelector<HTMLButtonElement>('[role="tab"][aria-selected="true"]')?.focus({ preventScroll: true })
  }, [open, host])

  useEffect(() => {
    if (!open) return
    const outside = (target: EventTarget | null) => target instanceof Node && !panel.current?.contains(target) && !trigger.current?.contains(target)
    const onPointer = (e: PointerEvent) => { if (outside(e.target)) close() }
    // Окно не модальное: Tab может уйти в текущий чат, закрыв входящие.
    const onFocus = (e: FocusEvent) => { if (outside(e.target)) close() }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (document.activeElement === document.body || !outside(document.activeElement))) {
        e.preventDefault(); e.stopImmediatePropagation(); close(true)
      }
    }
    const onResize = () => close()
    const app = useAppStore.subscribe((s, prev) => {
      if (s.view !== prev.view || s.settings !== prev.settings || s.pane !== prev.pane) close()
    })
    const auth = useAuthStore.subscribe((s, prev) => { if (s.user?.id !== prev.user?.id) close() })
    document.addEventListener('pointerdown', onPointer, true)
    document.addEventListener('focusin', onFocus)
    window.addEventListener('keydown', onKey, true)
    window.addEventListener('resize', onResize)
    return () => {
      app(); auth()
      document.removeEventListener('pointerdown', onPointer, true)
      document.removeEventListener('focusin', onFocus)
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener('resize', onResize)
    }
  }, [open, close])

  return <>
    <button ref={trigger} type="button" className={cx(className, open && activeClassName)} aria-label={label} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined} data-tooltip={tooltip && !open ? 'Входящие' : undefined} onClick={() => open ? close(true) : setOpen(true)}>{children}</button>
    {open ? createPortal(
      <div ref={panel} id={id} className={styles.popover} role="dialog" aria-label="Входящие">
        <InboxPage onClose={() => close(true)} onNavigate={() => close()} />
      </div>, host ?? document.body,
    ) : null}
  </>
}
